// ============================================================
// notificationWindows — group the notification feed by the quarter-hour
// window the fifteen-minute digest delivered it in.
//
// Shared by the web bell dropdown (frontend/src/components/shared/
// NotificationBell.jsx) and the iOS inbox (mobile/app/notifications.tsx), so
// the two cannot disagree about where a window starts or how it is named.
//
// The feed itself is unchanged — still one row per event, written the instant
// it happens — so the heading is DERIVED from each row's CreatedUTC rather
// than stored: the boundary is a property of the clock, not of the row.
// Labels use the DEVICE's local zone, which is the reader's.
// ============================================================

export const NOTIFICATION_WINDOW_MINUTES = 15;
const WINDOW_MS = NOTIFICATION_WINDOW_MINUTES * 60 * 1000;

/** Floor a timestamp to the start of its quarter-hour, or null when unreadable. */
export function notificationWindowStart(when: string | number | Date): Date | null {
  const t = when instanceof Date ? when.getTime() : new Date(when).getTime();
  if (Number.isNaN(t)) return null;
  return new Date(Math.floor(t / WINDOW_MS) * WINDOW_MS);
}

function clock(d: Date): string {
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/**
 * "Today · 2:45–3:00 PM", "Yesterday · 11:45 AM–12:00 PM", "Sep 14 · 9:00–9:15 AM".
 * When both ends share a meridiem it is written once.
 */
export function notificationWindowLabel(start: Date, now: Date = new Date()): string {
  const end = new Date(start.getTime() + WINDOW_MS);
  const a = clock(start);
  const b = clock(end);
  const merA = a.slice(-2), merB = b.slice(-2);
  const range = merA === merB ? `${a.slice(0, -3)}–${b}` : `${a}–${b}`;

  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  let day: string;
  if (sameDay(start, now)) day = "Today";
  else if (sameDay(start, yesterday)) day = "Yesterday";
  else day = start.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return `${day} · ${range}`;
}

export interface NotificationWindowGroup<T> {
  /** ISO of the window start — stable across renders, safe as a React key. */
  key: string;
  label: string;
  start: Date;
  items: T[];
}

/**
 * Group feed rows (newest-first, as the API returns them) by window. Order is
 * preserved; consecutive rows in the same window share a group. A row whose
 * timestamp cannot be read gets its own "Earlier" group rather than being
 * dropped — a notification that disappears from the inbox is worse than one
 * under a vague heading.
 */
export function groupByNotificationWindow<T extends { createdUtc: string }>(
  items: readonly T[] | null | undefined,
  now: Date = new Date(),
): NotificationWindowGroup<T>[] {
  const out: NotificationWindowGroup<T>[] = [];
  for (const it of items ?? []) {
    const start = notificationWindowStart(it.createdUtc);
    const key = start ? start.toISOString() : "unknown";
    const last = out[out.length - 1];
    if (last && last.key === key) { last.items.push(it); continue; }
    out.push({
      key,
      label: start ? notificationWindowLabel(start, now) : "Earlier",
      start: start ?? new Date(0),
      items: [it],
    });
  }
  return out;
}
